import {appFetch} from "../../utils/appFetch";

export interface AppFetchResponse {
    data: any;
    status: number;
}

export type V2TriggerDto = {
    id: number;
    shop_id: number;
    automation_id: number;
    title: string;
    entry_condition_id: number;
    exit_condition_id: number | null;
    cooldown_seconds: number;
    max_active_runs_per_client: number;
    active: 0 | 1;
    deleted: 0 | 1;
    created_at?: string;
    updated_at?: string;
    entry_event_name?: string | null;
    entry_event_basic_condition_id?: number | null;

};

function unwrap<T>(res: AppFetchResponse): { ok: boolean; data?: T; error?: string } {
    const ok = res.status >= 200 && res.status < 300;
    const payload = res?.data;
    if (payload && typeof payload === "object" && "data" in payload) {
        return { ok, data: (payload as any).data as T, error: (payload as any).error };
    }
    return { ok, data: payload as T, error: (payload as any)?.error };
}

export const GetV2AutomationTriggers = async (domain: string, automationId: number) => {
    const res: AppFetchResponse = await appFetch({
        endpoint: `v2/automations/${automationId}/triggers`,
        method: "GET",
        headers: { "Content-Type": "application/json" },
        domain,
    });
    return unwrap<V2TriggerDto[]>(res);
};

export const CreateV2AutomationTrigger = async (
    domain: string,
    automationId: number,
    body: {
        title: string;
        event_name?: string | null;
        cooldown_seconds?: number;
        max_active_runs_per_client?: number;
        active?: 0 | 1;
    }
) => {
    const res: AppFetchResponse = await appFetch({
        endpoint: `v2/automations/${automationId}/triggers`,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        domain,
        params: body,
    });
    return unwrap<{ id: number; triggerId?: number; entry_condition_id: number; entry_event_name?: string | null }>(res);
};

export const UpdateV2Trigger = async (
    domain: string,
    triggerId: number,
    patch: Partial<{
        title: string;
        cooldown_seconds: number;
        max_active_runs_per_client: number;
        active: 0 | 1;
        exit_condition_id: number | null;
    }>
) => {
    const res: AppFetchResponse = await appFetch({
        endpoint: `v2/triggers/${triggerId}`,
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        domain,
        params: patch,
    });
    return unwrap<{ ok: true }>(res);
};

export const DeleteV2Trigger = async (domain: string, triggerId: number) => {
    const res: AppFetchResponse = await appFetch({
        endpoint: `v2/triggers/${triggerId}`,
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        domain,
    });
    return unwrap<{ ok: true }>(res);
};

export const EnsureTriggerExitCondition = async (domain: string, triggerId: number) => {
    const res: AppFetchResponse = await appFetch({
        endpoint: `v2/triggers/${triggerId}/ensure-exit-condition`,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        domain,
    });

    return unwrap<{ exit_condition_id: number }>(res);
};

export const UpdateV2TriggerEntryEvent = async (
    domain: string,
    triggerId: number,
    body: { event_name: string }
) => {
    const res: AppFetchResponse = await appFetch({
        endpoint: `v2/triggers/${triggerId}/entry-event`,
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        domain,
        params: body,
    });

    return unwrap<{ ok: true }>(res);
};
